// Fallimenti delle risorse della pagina e violazioni della Content Security
// Policy (opt-in `captureResourceErrors`, CYJS-12). Sono i guasti che il
// browser segnala e che nessuno stack trace racconta: uno script di terze parti
// che non risponde, un foglio di stile che non arriva, una policy che blocca
// qualcosa che alla pagina serviva.
//
// Il canale è quello degli eventi, come per gli errori JS: sono fallimenti, e li
// si vuole raggruppati per risorsa e visti accanto agli altri. Passano da
// `captureException`, quindi ereditano campionamento, `excludedExceptions`
// (`ResourceError` / `CSPViolation` spengono una famiglia sola), `beforeSend`,
// scrubbing e contatori — senza una riga di pipeline in più.
//
// Livello `warning` e non `error`: una risorsa bloccata è quasi sempre ambiente
// o terze parti (un blocco pubblicità, una CDN irraggiungibile), non un difetto
// dell'applicazione. Alzarla a errore riempirebbe l'alerting di rumore che
// nessuno può correggere.
//
// L'evento `error` di una risorsa NON risale il DOM: si intercetta in fase di
// cattura su window, come già fa `globalHandlers` — che invece questi eventi li
// scarta, perché senza `error`/`message` non c'è niente da normalizzare.

import type { Client } from '../../client'

// Tetto di segnalazioni per caricamento pagina, **per famiglia**. Una CDN
// irraggiungibile fa fallire ogni immagine del catalogo: la prima decina dice
// già tutto, le altre sarebbero solo traffico e quota bruciata su un ingest che
// non ne sa di più. Il tetto è per famiglia e non condiviso perché una CDN giù
// non deve nascondere una policy che blocca: sono due problemi diversi, e chi
// arriva secondo non ha colpa di quanto è rumoroso il primo.
const MAX_FAILURES_PER_PAGE = 10

/// Etichette leggibili per gli elementi che sanno fallire nel caricamento. Il
/// nome finisce nel messaggio dell'evento, che è la chiave con cui il backend
/// raggruppa: "script non caricato: …" resta lo stesso gruppo a ogni occorrenza.
const RESOURCE_LABELS: Record<string, string> = {
  script: 'script non caricato',
  link: 'foglio di stile non caricato',
  img: 'immagine non caricata',
  image: 'immagine non caricata',
  audio: 'audio non caricato',
  video: 'video non caricato',
  source: 'sorgente multimediale non caricata',
  track: 'traccia non caricata',
  iframe: 'contenuto incorporato non caricato',
  object: 'oggetto non caricato',
  embed: 'oggetto non caricato',
}

/// Installa i listener (risorse + CSP). Ritorna la funzione di disinstallazione.
/// No-op fuori dal browser.
export function installResourceErrors(client: Client): () => void {
  if (typeof window === 'undefined') return () => {}

  // Chiavi già segnalate in questa pagina, una raccolta per famiglia: fa da
  // deduplica (la stessa risorsa che ritenta non vale una nuova segnalazione) e,
  // con la sua capienza, da tetto — che resta separato tra risorse e policy.
  const reported: Record<string, Set<string>> = {
    ResourceError: new Set(),
    CSPViolation: new Set(),
  }

  const capture = (
    name: 'ResourceError' | 'CSPViolation',
    key: string,
    message: string,
    contexts: Record<string, unknown>,
  ): void => {
    const seen = reported[name]!
    if (seen.has(key)) return
    if (seen.size >= MAX_FAILURES_PER_PAGE) return
    seen.add(key)
    client.captureException(
      { name, message },
      { level: 'warning', handled: true, contexts },
    )
  }

  const onError = (event: Event): void => {
    // Errore JavaScript vero: è di `globalHandlers`, qui non si tocca.
    const errorEvent = event as Partial<ErrorEvent> & Event
    if (errorEvent.error || errorEvent.message) return

    const target = event.target
    if (!target || target === window || !isElement(target)) return

    const url = resourceUrl(target)
    if (!url) return

    const element = target.tagName.toLowerCase()
    const label = RESOURCE_LABELS[element] ?? 'risorsa non caricata'
    capture('ResourceError', url, `${label}: ${url}`, {
      resource: { url, element },
    })
  }

  const onViolation = (event: Event): void => {
    const violation = event as Event & Partial<SecurityPolicyViolationEvent>
    const directive =
      violation.effectiveDirective || violation.violatedDirective || 'policy'
    const blocked = violation.blockedURI ? stripQuery(violation.blockedURI) : 'inline'
    const sourceFile = violation.sourceFile
      ? stripQuery(violation.sourceFile)
      : undefined

    capture(
      'CSPViolation',
      `${directive}:${blocked}`,
      `${directive} ha bloccato ${blocked}`,
      {
        csp: {
          blocked_uri: blocked,
          effective_directive: directive,
          ...(violation.disposition ? { disposition: violation.disposition } : {}),
          ...(sourceFile ? { source_file: sourceFile } : {}),
          ...(violation.lineNumber ? { line_number: violation.lineNumber } : {}),
        },
      },
    )
  }

  window.addEventListener('error', onError, true)
  if (typeof document !== 'undefined') {
    document.addEventListener('securitypolicyviolation', onViolation)
  }

  return () => {
    window.removeEventListener('error', onError, true)
    if (typeof document !== 'undefined') {
      document.removeEventListener('securitypolicyviolation', onViolation)
    }
  }
}

function isElement(target: EventTarget): target is Element {
  return typeof (target as Element).tagName === 'string'
}

// Indirizzo della risorsa fallita: `currentSrc` per primo — con `srcset` è il
// browser a scegliere la variante, e l'attributo `src` può essere assente o
// indicare un altro file — poi `src`, `href` per i fogli di stile, `data` per
// gli oggetti incorporati. Sempre senza query string: può portare token di firma
// delle CDN, e per sapere quale risorsa manca non serve.
function resourceUrl(element: Element): string | undefined {
  const current = (element as { currentSrc?: unknown }).currentSrc
  const raw =
    (typeof current === 'string' && current ? current : null) ??
    element.getAttribute('src') ??
    element.getAttribute('href') ??
    element.getAttribute('data')
  if (!raw) return undefined
  return stripQuery(raw)
}

function stripQuery(url: string): string {
  return url.split(/[?#]/, 1)[0] ?? url
}
